import { useEffect, useReducer, useState } from 'react';
import { hubService } from '../../services/hub.service';
import { INITIAL_JOB_STATE, closesStream, createSseParser, isTerminal, jobStreamReducer, type JobEvent } from '../../lib/jobView';

const MAX_RECONNECTS = 20; // consecutive tries without a new event
const RECONNECT_MS = 1500;

/**
 * A builder job's live progress, read from the hub's relay of llm-tester's stream (fetch + reader: EventSource cannot
 * send the bearer). Rules kept from v1 useJobStream: reconnect with `lastEventId` so nothing replays twice; the
 * client closes only on success / cancel; when the server ends the stream right after a terminal event, that close is
 * the end (reconnecting there replayed v1 job c45aa503 in a loop). Any 4xx (not this bot's job, not allowed) stops for
 * good, and so does running out of retries — both reported as `streamError` instead of "connecting" forever.
 * Remount with `key={jobId}` for a new job.
 */
export function useJobStream(system: string, botId: string, jobId: string | null) {
  const [state, dispatch] = useReducer(jobStreamReducer, INITIAL_JOB_STATE);
  const [streamError, setStreamError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) return undefined;
    const ac = new AbortController();
    let lastEventId: string | null = null;
    let attempts = 0;
    let done = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const connect = async () => {
      if (done || ac.signal.aborted) return;
      let sawTerminal = false;
      try {
        const res = await hubService.openKnowledgeJobEvents(system, botId, jobId, lastEventId, ac.signal);
        if (res.status >= 400 && res.status < 500) {
          done = true;
          setStreamError(res.status === 404 ? 'This job is not found for this bot.' : `Live progress refused (${res.status}).`);
          return;
        }
        if (!res.ok || !res.body) throw new Error(`events ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        const parser = createSseParser();
        for (;;) {
          const { done: end, value } = await reader.read();
          if (end) break;
          for (const frame of parser.push(decoder.decode(value, { stream: true }))) {
            let event: JobEvent;
            try {
              event = JSON.parse(frame.data) as JobEvent;
            } catch {
              continue; // a malformed frame is skipped, not fatal
            }
            attempts = 0;
            if (frame.id) lastEventId = frame.id;
            dispatch(event);
            sawTerminal = isTerminal(event.type);
            if (closesStream(event.type)) {
              done = true;
              ac.abort();
              return;
            }
          }
        }
        if (sawTerminal) {
          done = true;
          return;
        }
      } catch {
        if (ac.signal.aborted) return;
      }
      if (done) return;
      if (attempts < MAX_RECONNECTS) {
        attempts += 1;
        timer = setTimeout(() => void connect(), RECONNECT_MS);
      } else {
        setStreamError('Live progress is unavailable right now — the job list still updates.');
      }
    };

    void connect();
    return () => {
      done = true;
      if (timer) clearTimeout(timer);
      ac.abort();
    };
  }, [system, botId, jobId]);

  return { ...state, streamError };
}
